import {
  Component,
  Host,
  h,
  State,
  ComponentInterface,
  Event,
  EventEmitter,
  Prop,
} from "@stencil/core";
import { ICountryCode, ISelectOption } from "../../../models";
import {
  ScheduleService,
  AppConfigService as Config,
  Log,
} from "../../../services";
import { Subscription } from "rxjs";

interface IState {
  countryDialCodes?: ISelectOption[];
  isLoading?: boolean;
}

const DEFAULT_STATE: IState = {
  countryDialCodes: [],
  isLoading: false,
};

@Component({
  tag: "av-select-country-dial-code",
  styleUrl: "av-select-country-dial-code.scss",
  shadow: true,
})
export class AvSelectCountryDialCode implements ComponentInterface {
  @Prop() countryCode: string;

  @State() state: IState = { ...DEFAULT_STATE };

  @Event({ bubbles: false }) codeChange: EventEmitter<ISelectOption>;

  private countryCodesRAW: ICountryCode[];
  private configSubscription: Subscription;
  private countryDialCodeSelection: ISelectOption;

  private setState(state: IState) {
    this.state = { ...this.state, ...state };
  }

  componentWillLoad(): void | Promise<void> {
    this.getCountryCodes();
  }

  disconnectedCallback(): void {
    this.configSubscription.unsubscribe();
  }

  private async getCountryCodes() {
    try {
      this.setState({ isLoading: true });
      this.countryCodesRAW = await ScheduleService.getCountryDialCodes();
      const countryDialCodes = this.countryCodesRAW.map((code) => {
        return {
          label: `${code.name} ${code.dial_code}`,
          value: code.dial_code,
        };
      });
      this.setState({ countryDialCodes });
      this.configSubscription = Config.optionsReady.subscribe((options) => {
        !!options && this.setDefaultCountryCode();
      });
    } catch (ex) {
      Log.error(ex);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private setDefaultCountryCode() {
    if (!!this.countryDialCodeSelection) {
      return;
    }

    let defaultCode;

    if (!!this.countryCode) {
      defaultCode = this.countryCodesRAW.find(
        (c) => c.dial_code === this.countryCode
      );
    } else if (!!Config.country) {
      defaultCode = this.countryCodesRAW.find(
        (c) => c.code.toLowerCase() === Config.country.toLowerCase()
      );
    }

    // check again in case the config.country or countryCode is not valid
    if (!defaultCode) {
      defaultCode = this.countryCodesRAW[0];
    }
    this.countryDialCodeSelection = this.state.countryDialCodes.find(
      (c) => c.value === defaultCode.dial_code
    );
    this.codeChange.emit(this.countryDialCodeSelection);
  }

  handleCountryDialCodeChange(e: CustomEvent) {
    this.countryDialCodeSelection = e.detail;
    this.codeChange.emit(this.countryDialCodeSelection);
  }

  render() {
    return (
      <Host>
        {!this.state.isLoading && this.state.countryDialCodes.length > 0 && (
          <av-select
            options={this.state.countryDialCodes}
            selectedLabel="value"
            selected={this.countryDialCodeSelection}
            onChangeComplete={this.handleCountryDialCodeChange.bind(this)}
          ></av-select>
        )}
        {this.state.isLoading && <av-spinner></av-spinner>}
      </Host>
    );
  }
}
